"use client"

import { BlockRenderer, DynamicThemeProvider } from "ui"
import { useSiteAnalytics } from "ui/layout/SiteAnalyticsContext"
import type { CmsThemeData } from "utils"
import { extractMetaTags, extractThemeData } from "utils"
import { extractProperty } from "utils/extractProperty"
import { findProperty } from "utils/findProperty"
import { LandingAnalyticsProvider } from "./analytics"
import About from "./blocks/About"
import AboutOpening1 from "./blocks/AboutOpening1"
import AboutOpening2 from "./blocks/AboutOpening2"
import AboutOpening3 from "./blocks/AboutOpening3"
import AboutOpening5 from "./blocks/AboutOpening5"
import AboutOpening6 from "./blocks/AboutOpening6"
import AboutOpeningComponent4 from "./blocks/AboutOpeningComponent4"
import AdditionalProjects1 from "./blocks/AdditionalProjects1"
import AdditionalProjects2 from "./blocks/AdditionalProjects2"
import AshtromCompanies from "./blocks/AshtromCompanies"
import BenefitsIcons from "./blocks/BenefitsIcons"
import BenefitsImages from "./blocks/BenefitsImages"
import Explanation from "./blocks/Explanation"
import Faq from "./blocks/Faq"
import Gallery1 from "./blocks/Gallery1"
import Gallery2 from "./blocks/Gallery2"
import Gallery3 from "./blocks/Gallery3"
import Gallery4 from "./blocks/Gallery4"
import Gallery5 from "./blocks/Gallery5"
import Gallery6 from "./blocks/Gallery6"
import HeroOption1 from "./blocks/HeroOption1"
import HeroOption2 from "./blocks/HeroOption2"
import HeroOption3 from "./blocks/HeroOption3"
import HeroOption4 from "./blocks/HeroOption4"
import HeroOption5 from "./blocks/HeroOption5"
import InteractiveMap from "./blocks/InteractiveMap"
import LandingFooter from "./blocks/LandingFooter"
import LandingHeader from "./blocks/LandingHeader"
import LandingHeader2 from "./blocks/LandingHeader2"
import LeadForm from "./blocks/LeadForm"
import PowerNumbers1 from "./blocks/PowerNumbers1"
import PowerNumbers2 from "./blocks/PowerNumbers2"
import Reviews from "./blocks/Reviews"
import StickyContact from "./blocks/StickyContact"
import ScriptInjection from "./ScriptInjection"

type Props = {
  locale: string
  data: any
}

const marketingComponents: Record<string, React.ComponentType<any>> = {
  heroOption1: HeroOption1,
  heroOption2: HeroOption2,
  heroOption3: HeroOption3,
  heroOption4: HeroOption4,
  heroOption5: HeroOption5,
  benefitsIcons: BenefitsIcons,
  benefitsImages: BenefitsImages,
  ashtromCompanies: AshtromCompanies,
  interactiveMap: InteractiveMap,
  powerNumbers1: PowerNumbers1,
  powerNumbers2: PowerNumbers2,
  landingFooter: LandingFooter,
  headerOption1: LandingHeader,
  headerOption2: LandingHeader2,
  reviews: Reviews,
  additionalProjects1: AdditionalProjects1,
  additionalProjects2: AdditionalProjects2,
  about: About,
  aboutOpening1: AboutOpening1,
  aboutOpening2: AboutOpening2,
  aboutOpening3: AboutOpening3,
  aboutOpeningComponent4: AboutOpeningComponent4,
  // Components 1-3 were aliased `aboutOpeningN` but 4 as `aboutOpeningComponentN`;
  // register both spellings for 5 so either CMS alias resolves.
  aboutOpening5: AboutOpening5,
  aboutOpeningComponent5: AboutOpening5,
  aboutOpening6: AboutOpening6,
  aboutOpeningComponent6: AboutOpening6,
  explanation: Explanation,
  gallery1: Gallery1,
  gallery2: Gallery2,
  gallery3: Gallery3,
  gallery4: Gallery4,
  gallery6: Gallery6,
  gallery5: Gallery5,
  leadForm: LeadForm,
  stickyContact: StickyContact,
  stickyContactWithPhone: StickyContact,
  // Umbraco aliased the "FAQ" doc type as `fAQ`; `faq` kept as a defensive alias
  faq: Faq,
  fAQ: Faq,
}

function getBlockPackageClass(block: any): string {
  const componentPkg = extractProperty(block, "colorPackage") || "1"
  const innerPkg = extractProperty(block, "innerColorPackage") || componentPkg
  return `mlp-pkg-${componentPkg} mlp-inner-pkg-${innerPkg}`
}

/* StickyContact renders as a fixed full-width bar at the bottom on mobile, so
   it covered the end of the last block — the Lead Form when it sits last
   (ASH-2137). Reserve its height at the end of the scroll container. Height is
   the bar's own box: 12px padding top and bottom plus a 40px button. The bar
   is hidden from 1024px up, where it becomes a floating pill, so the spacer
   goes too. */
const STICKY_BLOCK_ALIASES = ["stickyContact", "stickyContactWithPhone"]
const STICKY_BAR_HEIGHT = 64

const PACKAGE_STYLES = `
  .mlp-pkg-1 { --comp-bg: var(--pkg1-bg); --comp-text: var(--pkg1-text); --comp-heading: var(--pkg1-heading); }
  .mlp-pkg-2 { --comp-bg: var(--pkg2-bg); --comp-text: var(--pkg2-text); --comp-heading: var(--pkg2-heading); }
  .mlp-pkg-3 { --comp-bg: var(--pkg3-bg); --comp-text: var(--pkg3-text); --comp-heading: var(--pkg3-heading); }
  .mlp-inner-pkg-1 { --inner-bg: var(--pkg1-bg); --inner-text: var(--pkg1-text); --inner-heading: var(--pkg1-heading); }
  .mlp-inner-pkg-2 { --inner-bg: var(--pkg2-bg); --inner-text: var(--pkg2-text); --inner-heading: var(--pkg2-heading); }
  .mlp-inner-pkg-3 { --inner-bg: var(--pkg3-bg); --inner-text: var(--pkg3-text); --inner-heading: var(--pkg3-heading); }
  /* higher specificity than the #mlp-root * font rule, so Narkis stat numbers survive the page-font override */
  #mlp-root .mlp-font-narkis { font-family: var(--narkis) !important; }
  #mlp-root .mlp-font-narkis-cond { font-family: var(--narkis-condensed) !important; }
  /* carries the last block's package class so the reserved strip continues that
     block's surface colour instead of showing a seam of the root background */
  .mlp-sticky-spacer { height: ${STICKY_BAR_HEIGHT}px; background-color: var(--comp-bg); }
  @media (min-width: 1024px) { .mlp-sticky-spacer { display: none; } }
`

const MarketingLandingPage = ({ data, locale }: Props) => {
  const themeData: CmsThemeData = extractThemeData(data.page)
  const blocks = findProperty(data.page, "blocks")?.blocks || []
  const dir = locale === "he" ? "rtl" : "ltr"
  const pageBmbyId = extractProperty(data.page, "bmbyId") as number | undefined
  const pageCompanyId = extractProperty(data.page, "companyId") as number | undefined

  /* Same derivation the route's generateMetadata uses for the document title,
     so the review schema names the page the same way the <title> does. */
  const pageMeta = extractMetaTags({ itemsData: data })
  const pageTitle = pageMeta.metaTitle || pageMeta.titleBreadcrumbs || ""

  /* Shared analytics context (ASH-1854).

     company_name is the app currently serving the page, published by Layout -
     the page's own groupSite field is the list of subsidiaries allowed to serve
     it, so it cannot answer "who is showing this".

     project_type is the landing page slug, the last segment of its route
     (/{culture}/shared/general-information/marketing-landing-pages/{slug}/).
     GET_PAGE_BY_ROUTE selects `name` on children but not on the page node, so
     the slug is the unique per-page identifier available without a query
     change. */
  const { companyName: siteCompanyName } = useSiteAnalytics()
  const analyticsProjectType =
    (data.page?.url as string | undefined)?.split("/").filter(Boolean).pop() || ""

  const hasStickyBar = blocks.some((block: any) => STICKY_BLOCK_ALIASES.includes(block?.blockType))
  /* the sticky block itself is fixed and occupies no flow space, so the spacer
     must match the last block that actually ends the page */
  const lastContentBlock = [...blocks]
    .reverse()
    .find((block: any) => !STICKY_BLOCK_ALIASES.includes(block?.blockType))

  return (
    <DynamicThemeProvider locale={locale} themeData={themeData}>
      <LandingAnalyticsProvider
        companyName={siteCompanyName}
        projectType={analyticsProjectType}
        language={locale}
      >
        <ScriptInjection pageData={data.page} />
        <div
          id="mlp-root"
          dir={dir}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            overflowY: "scroll",
            overscrollBehavior: "none",
            WebkitOverflowScrolling: "touch",
            backgroundColor: themeData.packages[0].backgroundColor,
            direction: dir,
          }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `#mlp-root, #mlp-root * { font-family: var(--mlp-font) !important; }
            .grecaptcha-badge {  position: absolute !important; }
            ${PACKAGE_STYLES}`,
            }}
          />
          {blocks.map((block: any, index: number) => {
            const pkgClass = getBlockPackageClass(block)
            const sectionId = extractProperty(block, "sectionId") as string | undefined
            return (
              <div
                key={index}
                className={pkgClass}
                id={sectionId || undefined}
                /* Anchor links landed with the block's title tucked under the
                 sticky header (ASH-1913). Every block is an anchor target, so
                 the offset belongs here rather than in one component. */
                style={{ scrollMarginTop: 96 }}
              >
                <BlockRenderer
                  blockType={block.blockType}
                  blockData={block}
                  components={marketingComponents}
                  locale={locale}
                  {...(block.blockType === "leadForm"
                    ? { props: { bmbyId: pageBmbyId, companyId: pageCompanyId } }
                    : {})}
                  {...(block.blockType === "reviews" ? { props: { itemName: pageTitle } } : {})}
                />
              </div>
            )
          })}
          {hasStickyBar && lastContentBlock && (
            <div
              aria-hidden
              className={`mlp-sticky-spacer ${getBlockPackageClass(lastContentBlock)}`}
            />
          )}
        </div>
      </LandingAnalyticsProvider>
    </DynamicThemeProvider>
  )
}

export default MarketingLandingPage
